import React, { useEffect, useState, useMemo, useContext } from "react";
import { SubjectDataContext } from "./SubjectRoot";
import Chart from "chart.js/auto";
import "chartjs-adapter-date-fns";
import Global from "../utils/Global";
import dataLabelPlugin from "chartjs-plugin-datalabels";
import { RemoteCollectionTable } from "../components/Tables/RemoteCollectionTable";
import TwoLineGraph from "../components/Graphs/TwoLineGraph";
import LineGraph from "../components/Graphs/LineGraph";
import Trial from "../utils/Trial";
import { DateRangePicker } from "react-dates";
import PerformanceScoreGraph from "../components/Graphs/PerformanceScoreGraph";
import PerformanceScores from "../utils/PerformanceScores";

Chart.register(dataLabelPlugin);
const dayjs = require('dayjs');

export default function GaitMetrics() {
  // Get shared data from context
  const { subject, norm } = useContext(SubjectDataContext);

  // Local state for this component
  const [isTabLoading, setIsTabLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [performanceScores, setPerformanceScores] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [focusedInput, setFocusedInput] = useState(null);
  const [trialList, setTrialList] = useState(null);
  const [activeTab, setActiveTab] = useState("alltime");

  // Fetch tab-specific data
  useEffect(() => {
    const fetchTabData = async () => {
      try {
        setIsTabLoading(true);
        
        // Fetch data needed specifically for this tab
        const trialList = await Global.localAppHandler.app_get_trial_list({ include_metrics: true });
        const extracted_metrics = Trial.extractMetricsFromList(trialList);
        const performanceScores = await Global.localAppHandler.app_retrieve_performance_scores();
        const extracted_performance_scores = PerformanceScores.extractScoresFromList(performanceScores.performance_scores);
        
        setMetrics(extracted_metrics);
        setPerformanceScores(extracted_performance_scores);
        setTrialList(trialList);
      } catch (error) {
        console.error("Error fetching gait metrics data:", error);
      } finally {
        setIsTabLoading(false);
      }
    };

    // Only fetch if we have the necessary prerequisites
    if (subject && norm) {
      fetchTabData();
    }
  }, [subject, norm]);

  const handleTabClick = (tabIndex) => {
    setStartDate(null);
    setEndDate(null);
    setActiveTab(tabIndex);
  };

  const [minDate, maxDate, paddedMinDateStr, paddedMaxDateStr] = useMemo(() => {
    let dates;
    if (!metrics) {
      dates = [];
    } else {
      dates = metrics.dates
      .map((label) => new Date(`${label}T00:00:00`))
      .filter((date) => !isNaN(date.getTime()));
    }
    let min, max;
    if (activeTab === "alltime") {
      if (dates.length > 0) {
        min = new Date(Math.min(...dates.map((date) => date.getTime())));
        max = new Date(Math.max(...dates.map((date) => date.getTime())));
      } else {
        // Set to default values if no valid dates are found
        min = new Date();
        max = new Date();
      }
    } else if (activeTab === "7day") {
      if (dates.length > 0) {
        max = new Date();
        min = new Date(max);
        min.setDate(min.getDate() - 6); // 6 because end date is included
      } else {
        // Set to default values if no valid dates are found
        min = new Date();
        max = new Date();
      }
    } else if (activeTab === "30day") {
      if (dates.length > 0) {
        max = new Date();
        min = new Date(max);
        min.setDate(min.getDate() - 29); // 29 because end date is included
      } else {
        // Set to default values if no valid dates are found
        min = new Date();
        max = new Date();
      }
    } else if (activeTab === "90day") {
      if (dates.length > 0) {
        max = new Date();
        min = new Date(max);
        min.setDate(min.getDate() - 89); // 89 because end date is included
      } else {
        // Set to default values if no valid dates are found
        min = new Date();
        max = new Date();
      }
    } else if (activeTab === "select") {
      const isStartDateValid = startDate && !isNaN(new Date(startDate).getTime());
      const isEndDateValid = endDate && !isNaN(new Date(endDate).getTime());

      if (
        isStartDateValid &&
        isEndDateValid &&
        new Date(startDate) <= new Date(endDate) &&
        new Date(endDate) - new Date(startDate) <= 10 * 365 * 24 * 60 * 60 * 1000 // Max 10 years
      ) {
        min = new Date(startDate);
        max = new Date(endDate);
      } else {
        min = new Date();
        max = new Date();
      }
    }
    const range = max.getTime() - min.getTime();
    let padding = (0.2 * range) / (1000 * 60 * 60 * 24); // converting from ms to days

    if (padding < 2) {
      padding = 2;
    }
    let minDatePadded = new Date(min); // Clone the Date object
    minDatePadded.setDate(minDatePadded.getDate() - padding);

    let maxDatePadded = new Date(max); // Clone the Date object
    maxDatePadded.setDate(maxDatePadded.getDate() + padding);

    return [min, max, minDatePadded.toISOString(), maxDatePadded.toISOString()];
  }, [metrics, activeTab, startDate, endDate]);

  // Show loading state
  if (isTabLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px", marginTop: "100px" }}>
        <div className="text-center">
          <div className="spinner-border mb-3" role="status" style={{ width: "3rem", height: "3rem" }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading patient data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-xl px-4">
      <div className="card mb-4">
        <div className="card-body">
          <div className="row justify-content-center align-items-center">
            {/* Tabs Column (stretches, centered content) */}
            <div className="col-auto">
              <div className="btn-group" role="group" aria-label="Basic example">
                <div className={`btn btn-primary ${activeTab === "alltime" ? "active" : ""}`} onClick={() => handleTabClick("alltime")}>
                  All Time
                </div>
                <div className={`btn btn-primary ${activeTab === "7day" ? "active" : ""}`} onClick={() => handleTabClick("7day")}>
                  7 Day
                </div>
                <div className={`btn btn-primary ${activeTab === "30day" ? "active" : ""}`} onClick={() => handleTabClick("30day")}>
                  30 Day
                </div>
                <div className={`btn btn-primary ${activeTab === "90day" ? "active" : ""}`} onClick={() => handleTabClick("90day")}>
                  90 Day
                </div>
                <div className={`ml-2 btn btn-blue ${activeTab === "select" ? "active" : ""}`} onClick={() => handleTabClick("select")}>
                  Select Dates
                </div>
              </div>
            </div>

            {/* Date Selector Coumn (fixed width) */}
            <div className="col-auto" style={{ fontSize: "0.8em" }}>
              <DateRangePicker
                startDate={startDate}
                startDateId="start_date_id"
                disabled={activeTab !== "select"}
                endDate={endDate}
                endDateId="end_date_id"
                onDatesChange={({ startDate, endDate }) => {
                  setStartDate(startDate);
                  setEndDate(endDate);
                }}
                focusedInput={focusedInput}
                onFocusChange={(focusedInput) => {
                  setFocusedInput(focusedInput);
                }}
                numberOfMonths={1} // Show 1 month at a time
                isOutsideRange={() => false} // Allow past/future date selection
                displayFormat="MMM D, YYYY" // How dates are displayed (moment format)
              />
            </div>
          </div>
        </div>
      </div>
      <div className="overviewChartBox">
        {
          norm && norm.all_valid ?
          <div className="row">
            <div className="col-lg-12">
              <PerformanceScoreGraph
                labels={performanceScores ? performanceScores.dates : []}
                data={performanceScores ? performanceScores.scores : []}
                trialNames={metrics ? metrics.trial_names : []}
                lastUpdatedTime={""}
                timeAxis={"day"}
                minDate={minDate}
                maxDate={maxDate}
                paddedMinDateStr={paddedMinDateStr}
                paddedMaxDateStr={paddedMaxDateStr}
              />
            </div>
          </div> :
          <></>
        }

        <div className="row">
          <div className="col-lg-12">
            <LineGraph
              labels={metrics ? metrics.dates : []}
              data={metrics ? metrics.stride_times : []}
              trialNames={metrics ? metrics.trial_names : []}
              lastUpdatedTime={""}
              timeAxis={"day"}
              minDate={minDate}
              maxDate={maxDate}
              paddedMinDateStr={paddedMinDateStr}
              paddedMaxDateStr={paddedMaxDateStr}
              norm={norm?.stride_time}
              title={"Stride Times"}
              description={"Stride time is taken for a full gait cycle – or foot contact of a limb to the following foot contact of the same limb. Stride time typically decreases with an increase in walking speed."}
              unit={" ms"}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-lg-12">
            <TwoLineGraph
              labels={metrics ? metrics.dates : []}
              data_l={metrics ? metrics.stance_times_left : []}
              data_r={metrics ? metrics.stance_times_right : []}
              trialNames={metrics ? metrics.trial_names : []}
              lastUpdatedTime={""}
              timeAxis={"day"}
              minDate={minDate}
              maxDate={maxDate}
              paddedMinDateStr={paddedMinDateStr}
              paddedMaxDateStr={paddedMaxDateStr}
              norm={norm?.stance_time}
              title={"Stance Times"}
              description={"Stance time is the amount of time a limb is in contact with the ground during the gait cycle and is measured separately for the left and right limb. Stance times that are too high or too low may indicate pain, issues with balance, or a lack of mobility in the affected limb."}
              unit={" ms"}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-lg-6">
            <LineGraph
              labels={metrics ? metrics.dates : []}
              data={metrics ? metrics.stance_asis : []}
              trialNames={metrics ? metrics.trial_names : []}
              lastUpdatedTime={""}
              timeAxis={"day"}
              minDate={minDate}
              maxDate={maxDate}
              paddedMinDateStr={paddedMinDateStr}
              paddedMaxDateStr={paddedMaxDateStr}
              norm={norm?.stance_asi}
              title={"Stance Asymmetry"}
              description={"Differences between left and right stance times lead to asymmetry that may indicate overcompensation and can result in overuse or strain of other systems. An ideal gait cycle will have asymmetries close to 0%."}
              unit={"%"}
            />
          </div>
          <div className="col-lg-6">
            <LineGraph
              labels={metrics ? metrics.dates : []}
              data={metrics ? metrics.swing_asis : []}
              trialNames={metrics ? metrics.trial_names : []}
              lastUpdatedTime={""}
              timeAxis={"day"}
              minDate={minDate}
              maxDate={maxDate}
              paddedMinDateStr={paddedMinDateStr}
              paddedMaxDateStr={paddedMaxDateStr}
              norm={norm?.swing_asi}
              title={"Swing Asymmetry"}
              description={"Differences between left and right swing times lead to asymmetry that may indicate overcompensation and can result in overuse or strain of other systems. An ideal gait cycle will have asymmetries close to 0%."}
              unit={"%"}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-lg-12">
            <LineGraph
              labels={metrics ? metrics.dates : []}
              data={metrics ? metrics.vas_scores : []}
              trialNames={metrics ? metrics.trial_names : []}
              timeAxis={"day"}
              minDate={minDate}
              maxDate={maxDate}
              paddedMinDateStr={paddedMinDateStr}
              paddedMaxDateStr={paddedMaxDateStr}
              title={"VAS Pain Scores"}
              description={"For every walking data collection, pain on a scale of 0 to 10 was reported."}
            />
          </div>
        </div>
        <div className="col-lg-12">
          <RemoteCollectionTable trialList={trialList ? trialList : []} minDate={minDate} maxDate={maxDate} />
        </div>
      </div>
    </div>
  );
}